import React, { useEffect, useMemo, useState } from "react";

function detectMode(value, availableParams = []) {
  const str = String(value ?? "").trim();
  if (!str) return "fixed";
  if (!Number.isNaN(Number(str))) return "fixed";
  if (availableParams.includes(str)) return "parameter";
  return "formula";
}

function evaluateExpression(expr, params = {}) {
  const str = String(expr ?? "").trim();
  if (!str) return { ok: true, value: "" };

  const direct = Number(str);
  if (!Number.isNaN(direct)) return { ok: true, value: direct };

  if (Object.prototype.hasOwnProperty.call(params, str)) {
    return { ok: true, value: params[str] };
  }

  try {
    const keys = Object.keys(params || {});
    const values = keys.map((k) => Number(params[k]) || 0);
    // eslint-disable-next-line no-new-func
    const fn = new Function(...keys, `return (${str});`);
    const result = fn(...values);

    if (!Number.isFinite(result)) {
      return { ok: false, value: "", message: "Formula result is not valid." };
    }

    return { ok: true, value: result };
  } catch {
    return { ok: false, value: "", message: "Invalid formula." };
  }
}

export default function ExpressionField({
  label,
  value,
  onChange,
  availableParams = [],
  previewParams = {},
  placeholder = "",
  disabled = false,
  onValidationChange,
}) {
  const [mode, setMode] = useState(() => detectMode(value, availableParams));
  const [localValue, setLocalValue] = useState(value ?? "");

  useEffect(() => {
    setLocalValue(value ?? "");
    if (String(value ?? "").trim()) {
      setMode(detectMode(value, availableParams));
    }
  }, [value, availableParams]);

  const preview = useMemo(
    () => evaluateExpression(localValue, previewParams),
    [localValue, previewParams]
  );

  const isEmpty = String(localValue ?? "").trim() === "";
  const isInvalid = mode === "formula" && !isEmpty && !preview.ok;

  useEffect(() => {
  onValidationChange?.({
    ok: !isInvalid,
    value: preview.value,
    message: preview.message || "",
  });
}, [isInvalid, preview.value, preview.message, onValidationChange]);

  function commit() {
    if (isInvalid || disabled) return;
    onChange?.(localValue);
  }

  function handleModeChange(nextMode) {
    setMode(nextMode);

    if (nextMode === "fixed") {
      setLocalValue("0");
      onChange?.("0");
      return;
    }

    if (nextMode === "parameter") {
      const first = availableParams[0] || "";
      setLocalValue(first);
      onChange?.(first);
      return;
    }

    setLocalValue("");
  }

  function appendToken(token) {
    const current = String(localValue || "").trim();
    setLocalValue(current ? `${current} ${token}` : token);
  }

  return (
    <div className="space-y-2">
      {label ? <label className="block text-sm font-medium">{label}</label> : null}

      <div className="grid grid-cols-3 gap-2">
        <select
          value={mode}
          disabled={disabled}
          onChange={(e) => handleModeChange(e.target.value)}
          className="px-3 py-2 border rounded"
        >
          <option value="fixed">fixed</option>
          <option value="parameter">parameter</option>
          <option value="formula">formula</option>
        </select>

        {mode === "parameter" ? (
          <select
            value={localValue || ""}
            disabled={disabled}
            onChange={(e) => {
              setLocalValue(e.target.value);
              onChange?.(e.target.value);
            }}
            className="col-span-2 px-3 py-2 border rounded"
          >
            {availableParams.map((param) => (
              <option key={param} value={param}>
                {param}
              </option>
            ))}
          </select>
        ) : (
          <input
            value={localValue ?? ""}
            disabled={disabled}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
            }}
            placeholder={placeholder || "Value or formula"}
            className={`col-span-2 px-3 py-2 border rounded font-mono text-sm ${
              isInvalid ? "border-red-500 bg-red-50" : ""
            }`}
          />
        )}
      </div>

      <div className={`text-xs ${isInvalid ? "text-red-600" : "text-gray-500"}`}>
        {isInvalid
          ? preview.message || "Invalid expression"
          : `Resolved: ${preview.value === "" ? "—" : preview.value}`}
      </div>

      {mode === "formula" && !disabled && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1">
            {["+", "-", "*", "/", "(", ")"].map((op) => (
              <button
                key={op}
                type="button"
                onClick={() => appendToken(op)}
                className="px-2 py-1 rounded border bg-white text-xs font-mono hover:bg-blue-50"
              >
                {op}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-1 max-h-28 overflow-auto">
            {availableParams.slice(0, 40).map((param) => (
              <button
                key={param}
                type="button"
                onClick={() => appendToken(param)}
                className="px-2 py-1 rounded border bg-white text-xs font-mono hover:bg-blue-50"
              >
                {param}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}